import type { VatCalculationRequest } from '../../lib/api';
import type { ReconciliationItem } from '../shared';
import { nairaToKoboExact } from '../track-b1';

export interface PracticeVatFactsInput {
  period: string;
  taxableOutputValueNaira: string;
  zeroRatedOutputValueNaira: string;
  exemptOutputValueNaira: string;
  allowableInputVatNaira: string;
}

export interface BoundVatFacts {
  request: VatCalculationRequest;
  periodEndExclusive: string;
}

export const initialPracticeVatFacts: PracticeVatFactsInput = {
  period: '2026-07',
  taxableOutputValueNaira: '',
  zeroRatedOutputValueNaira: '0',
  exemptOutputValueNaira: '0',
  allowableInputVatNaira: '0',
};

export interface ReviewedVatDerivation {
  values: PracticeVatFactsInput;
  reviewedItems: number;
  outstandingItems: number;
  excludedItems: number;
  sourceEvidenceIds: string[];
}

/**
 * Builds draft VAT facts only from proposals explicitly accepted by a human.
 * Classification confidence never promotes a proposal into the calculation.
 */
export function deriveReviewedVatFacts(items: readonly ReconciliationItem[], fallbackPeriod = '2026-07'):
  ReviewedVatDerivation {
  const accepted = items.filter((item) => ['accepted-by-human', 'corrected-by-human'].includes(item.state)
    && item.amountKobo !== undefined && item.vatAmountKobo !== undefined && item.direction !== undefined);
  let taxableOutput = 0n;
  let allowableInputVat = 0n;
  for (const item of accepted) {
    const amount = nonNegativeKobo(item.amountKobo!);
    const vat = nonNegativeKobo(item.vatAmountKobo!);
    if (item.direction === 'credit' && item.ntaCode === 'INC-SALES') taxableOutput += amount >= vat ? amount - vat : amount;
    if (item.direction === 'debit' && item.ntaCode?.startsWith('EXP-')) allowableInputVat += vat;
  }
  const dated = accepted.find((item) => /^\d{4}-\d{2}-\d{2}$/.test(item.transactionDate ?? ''));
  return {
    values: { period: dated?.transactionDate?.slice(0, 7) ?? fallbackPeriod,
      taxableOutputValueNaira: koboToNaira(taxableOutput), zeroRatedOutputValueNaira: '0',
      exemptOutputValueNaira: '0', allowableInputVatNaira: koboToNaira(allowableInputVat) },
    reviewedItems: accepted.length,
    outstandingItems: items.filter((item) => item.state === 'needs-review').length,
    excludedItems: items.filter((item) => ['blocked', 'corrected-by-human', 'excluded-by-human'].includes(item.state)).length,
    sourceEvidenceIds: [...new Set(accepted.flatMap((item) => item.evidenceId ? [item.evidenceId] : []))],
  };
}

export async function bindReviewedVatFacts(values: PracticeVatFactsInput, identity: {
  taxpayerId: string;
  engagementId: string;
}): Promise<BoundVatFacts> {
  if (!/^\d{4}-(?:0[1-9]|1[0-2])$/.test(values.period)) throw new Error('Select a valid VAT month.');
  const periodStart = `${values.period}-01`;
  const facts: VatCalculationRequest['facts'] = {
    kind: 'VAT',
    taxableOutputValueKobo: vatNairaToKobo(values.taxableOutputValueNaira, 'Taxable output value'),
    zeroRatedOutputValueKobo: vatNairaToKobo(values.zeroRatedOutputValueNaira, 'Zero-rated output value'),
    exemptOutputValueKobo: vatNairaToKobo(values.exemptOutputValueNaira, 'Exempt output value'),
    allowableInputVatKobo: vatNairaToKobo(values.allowableInputVatNaira, 'Allowable input VAT'),
  };
  const packageId = 'AMOYE-VAT-NRS-MONTHLY-CAND-001' as const;
  const reviewedFactSnapshotSha256 = await sha256(stableJson({
    schemaVersion: 'amoye-reviewed-vat-facts-1',
    taxpayerId: identity.taxpayerId,
    engagementId: identity.engagementId,
    packageId,
    periodStart,
    facts,
  }));
  return {
    request: { packageId, periodStart, reviewedFactSnapshotSha256, facts },
    periodEndExclusive: nextMonth(periodStart),
  };
}

function vatNairaToKobo(value: string, label: string): string {
  try { return nairaToKoboExact(value); }
  catch { throw new Error(`${label} must be entered in naira with no commas and no more than two decimal places.`); }
}

function nonNegativeKobo(value: string): bigint {
  return /^(0|[1-9]\d*)$/.test(value) ? BigInt(value) : 0n;
}

function koboToNaira(value: bigint): string {
  const whole = value / 100n; const fraction = value % 100n;
  return fraction === 0n ? whole.toString() : `${whole}.${fraction.toString().padStart(2, '0').replace(/0$/, '')}`;
}

function nextMonth(periodStart: string): string {
  const year = Number(periodStart.slice(0, 4));
  const month = Number(periodStart.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonthValue = month === 12 ? 1 : month + 1;
  return `${nextYear.toString().padStart(4, '0')}-${nextMonthValue.toString().padStart(2, '0')}-01`;
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`;
  return JSON.stringify(value);
}
